<template>
  <div class="content-view">
    <PtrSidebar>
      <router-view :key="refreshKey" ref="contentPage" class="px-4 left-content" :class="{ shrunk: isMenuShrunk }">
        <template
          #header="{
            goBack = true,
            shouldHideBackBtn = $route.name.includes('Site'),
            backButtonId = 'content-back-button'
          }"
        >
          <v-row
            no-gutters
            class="left-content__header flex-nowrap mt-3 align-center"
            :class="{ 'ml-3': shouldHideBackBtn }"
          >
            <v-col cols="auto" class="mr-4 ml-2" :style="{ display: shouldHideBackBtn ? 'none' : 'block' }">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      :id="backButtonId"
                      color="white"
                      class="back-button v-btn--square"
                      :disabled="isGoBackDisabled"
                      small
                      icon
                      depressed
                      plain
                      @click="goBack"
                    >
                      <PtrIcon icon="caret-left" />
                    </v-btn>
                  </span>
                </template>
                <span style="margin-top: -2px">{{ getGoBackTooltip }}</span>
              </v-tooltip>
            </v-col>
            <v-spacer />
          </v-row>
        </template>
        <template #buttons="{ isValid, save, goBack }">
          <v-card tile class="pa-4 ms-n4">
            <v-row>
              <v-col>
                <v-btn id="cancel" outlined block @click="goBack">{{ $t("cancel") }}</v-btn>
              </v-col>
              <v-col>
                <v-btn id="save" color="primary" depressed block :disabled="!isValid || !isFormDirty" @click="save">
                  {{ $t("save") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </template>
      </router-view>
    </PtrSidebar>

    <div class="right-content">
      <div
        class="left border"
        :class="{
          'no-add-edit-panel': isAddEdit
        }"
      ></div>
      <div
        class="top border"
        :class="{
          'no-add-edit-panel': isAddEdit
        }"
      ></div>
      <div class="right border"></div>
      <div
        class="bottom border"
        :class="{
          'no-add-edit-panel': isAddEdit
        }"
      ></div>
      <div :class="{ 'ai-in-progress': isAIMappingInProgress }"></div>

      <PtrBackgroundLoading v-if="backgroundLoadingRequestCount > 0" />
      <div
        class="map-wrapper"
        :class="{
          hidden: shouldDisplayLevelManager || shouldDisplayFloorAlignment
        }"
      >
        <div v-if="isDragging" id="drop-area" @drop="(event) => onFileDrop(event)"></div>
        <PtrMap
          v-else
          :highlighted-fid="contextMenuSelectedFid"
          :should-disable-mouse-move-highlight="shouldDisableMouseMoveHighlight"
          @context-menu-clicked="onMapContextMenu"
          @map-move="removePopupAndHighlight"
          @map-selection-changed="onMapSelectionChanged"
        />
        <MapUI
          :position="contextMenuPosition"
          :features="contextMenuFeatures"
          @copy="handleContextMenuCopy"
          @copy-selected-features="handleContextMenuCopySelectedFeatures"
          @paste-and-edit="handleContextMenuPasteAndEdit"
          @clone="handleContextMenuClone"
          @multiple-clone="handleContextMenuMultipleClone"
          @edit="handleContextMenuEdit"
          @multiple-edit="handleContextMenuMultipleEdit"
          @feature-selected="handleContextMenuFeatureSelected"
          @toggle-mouse-move-highlight="handleToggleMouseMoveHighlight"
        >
          <template #topLeftTowardsRight>
            <div v-if="shouldShowMapIcons.search.shouldShow">
              <MapSearch />
            </div>
          </template>
          <template #topLeftTowardsDown></template>

          <template #bottomLeftTowardsUp></template>

          <template #bottomLeftTowardsRight></template>

          <template #centerTop></template>

          <template #centerBottom>
            <div v-if="!isWhiteLabelEnabled" class="copyright-area">
              <PtrIcon :class="{ 'mb-4': !isStyleJsonLoaded }" icon="copyright-logo" width="64" />
              <div v-show="isStyleJsonLoaded" class="copyright-text">
                © Pointr {{ new Date().getFullYear() }} and
                {{ isEsriMap ? "Powered by Esri" : "© Maptiler© OpenStreetMap contributors" }}
              </div>
            </div>
          </template>

          <template #bottomRightTowardsUp>
            <PathLevelSelector v-if="shouldShowMapIcons.pathLevelSelector.shouldShow"></PathLevelSelector>
            <v-btn v-if="shouldShowMapIcons.help.shouldShow" id="map-ui-help" color="white" class="v-btn--square">
              <PtrIcon icon="help" editable-color />
            </v-btn>
            <v-btn
              v-if="shouldShowMapIcons.zoom.shouldShow"
              id="map-ui-zoom-in"
              color="white"
              class="v-btn--square"
              :disabled="zoom === maxZoom"
              @click="zoomIn"
            >
              <PtrIcon icon="plus" editable-color />
            </v-btn>
            <v-btn
              v-if="shouldShowMapIcons.zoom.shouldShow"
              id="map-ui-zoom-out"
              color="white"
              class="v-btn--square"
              :disabled="zoom === minZoom"
              @click="zoomOut"
            >
              <PtrIcon icon="minus" editable-color />
            </v-btn>
          </template>

          <template #bottomRightTowardsLeft></template>
        </MapUI>

        <GuidancePopup
          v-if="guidanceMessage || (guidanceText && !isAddEdit)"
          :no-add-edit-panel="isAddEdit"
          :display-text="guidanceText && !isAddEdit ? guidanceText : undefined"
        ></GuidancePopup>
        <MapSettings></MapSettings>
        <MapBuildingLevelSelector
          v-if="shouldShowMapBuildingLevelSelector || shouldShowMapIcons.buildingLevelSelector.shouldShow"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import PtrSidebar from "@/components/shared/PtrSidebar.vue";
import MapUI from "@/components/MapUI.vue";
import PtrMap from "@/components/PtrMap.vue";
import PtrIcon from "@/components/shared/PtrIcon.vue";
import MapSearch from "@/components/MapSearch.vue";
import PtrBackgroundLoading from "@/components/shared/PtrBackgroundLoading.vue";
import PathLevelSelector from "@/components/shared/PathLevelSelector.vue";
import MapSettings from "@/components/MapSettings/MapSettings.vue";
import MapModeConstants from "@/constants/mapModeConstants";
import MapHelpers from "@/helpers/MapHelpers";
import PointMode from "@/helpers/drawModes/PointMode";
import GraphMode from "@/helpers/drawModes/GraphMode";
import GuidancePopup from "@/components/shared/GuidancePopup.vue";
import MapBuildingLevelSelector from "@/components/shared/MapBuildingLevelSelector.vue";
import ContentService from "@/services/ContentService";

export default {
  name: "ContentView",
  components: {
    PathLevelSelector,
    PtrSidebar,
    MapUI,
    PtrMap,
    PtrIcon,
    MapSearch,
    PtrBackgroundLoading,
    GuidancePopup,
    MapSettings,
    MapBuildingLevelSelector
  },

  data: () => ({
    isMenuShrunk: false,
    displayAddEditPanel: false,
    breadcrumbTooltipText: "",
    shouldDisableTooltip: false,
    isWhiteLabelEnabled: false,
    contextMenuPosition: null,
    contextMenuFeatures: [],
    contextMenuSelectedFid: null,
    shouldDisableMouseMoveHighlight: false,
    guidanceText: undefined
  }),

  computed: {
    ...mapState("MAP", [
      "map",
      "currentSite",
      "currentClient",
      "mapMode",
      "shouldShowMapIcons",
      "zoom",
      "maxZoom",
      "minZoom",
      "drawnCoordinates",
      "isGraphDrawn",
      "isAIMappingInProgress",
      "guidanceMessage",
      "shouldDisplayLevelManager",
      "shouldDisplayFloorAlignment",
      "isEsriMap",
      "isStyleJsonLoaded"
    ]),
    ...mapGetters("MAP", ["currentSiteObject", "currentBuildingObject"]),
    ...mapState("CONTENT", ["refreshKey", "isFormDirty", "isDragging", "selectedFeatures", "copiedMapContentFeatures"]),
    isContentReadOnly() {
      return this.$store.state.isSelectedContentReadOnly;
    },
    loadingRequestCount() {
      return this.$store.state.loadingRequestCount || 0;
    },
    MAP_MODES() {
      return MapModeConstants.MAP_MODES;
    },
    isEditOrAddPage() {
      const path = window.location.pathname;
      return path.includes("edit") || path.includes("add");
    },
    getLevelSwitcherTooltip() {
      if (this.$route.name.includes("Edit") && !this.$route.name.includes("Client")) {
        return this.$t("button-tooltips.go-back-disabled-edit");
      } else if (this.$route.name.includes("Add")) {
        return this.$t("button-tooltips.go-back-disabled-add");
      } else if (this.$route.params.featureId) {
        if (this.$route.params.featureId === "add") {
          return this.$t("button-tooltips.go-back-disabled-add");
        } else {
          return this.$t("button-tooltips.go-back-disabled-edit");
        }
      } else {
        return this.breadcrumbTooltipText;
      }
    },
    getGoBackTooltip() {
      if (this.$route.name.includes("Edit") && !this.$route.name.includes("Client")) {
        return this.$t("button-tooltips.go-back-disabled-edit");
      } else if (this.$route.name.includes("Add")) {
        return this.$t("button-tooltips.go-back-disabled-add");
      } else if (this.$route.params.featureId) {
        if (this.$route.params.featureId === "add") {
          return this.$t("button-tooltips.go-back-disabled-add");
        } else {
          return this.$t("button-tooltips.go-back-disabled-edit");
        }
      } else {
        return this.$t("button-tooltips.go-back");
      }
    },
    backgroundLoadingRequestCount() {
      return this.$store.state.backgroundLoadingRequestCount || 0;
    },
    isGoBackDisabled() {
      return this.backgroundLoadingRequestCount > 0;
    },
    isAddEdit() {
      // TODO: remove this when all content add edit is taken out of content add edit panel
      const addEditRoutes = [
        "BuildingAdd",
        "BuildingEdit",
        "GpsGeofenceAdd",
        "GpsGeofenceEdit",
        "BeaconGeofenceAdd",
        "BeaconGeofenceEdit",
        "SiteAdd",
        "SiteEdit",
        "MapContentAdd",
        "MapContentEdit",
        "MapContentMultipleAdd",
        "MapContentMultipleEdit",
        "BeaconAdd",
        "BeaconEdit",
        "OutdoorPathAdd",
        "OutdoorPathEdit",
        "PathAdd",
        "PathEdit"
      ];
      return addEditRoutes.includes(this.$route.name);
    },
    shouldShowMapBuildingLevelSelector() {
      return (
        this.zoom >= 10 &&
        !!this.currentBuildingObject &&
        !this.$route.name.includes("Add") &&
        !this.$route.name.includes("Edit")
      );
    }
  },

  watch: {
    currentSite(newValue, oldValue) {
      if (newValue !== oldValue && oldValue !== undefined && !this.$route.name.includes("Site")) {
        this.$store.dispatch("MAP/SET_CURRENT_BUILDING", { building: undefined });
        this.$store.dispatch("MAP/SET_CURRENT_LEVEL", { level: undefined });
        MapHelpers.initalizeSiteMapData(this.currentClient, newValue);
      }
    },
    selectedFeatures(newValue) {
      if (newValue.length === 0 && this.guidanceText?.includes("item(s) selected")) {
        this.guidanceText = undefined;
      }
    },
    $route(newValue, oldValue) {
      if (newValue.name !== oldValue.name) {
        this.removePopupAndHighlight();
      }
    }
  },

  beforeDestroy() {
    clearInterval(this.contentStatusInterval);
    this.contentStatusInterval = null;
  },

  created() {
    this.isWhiteLabelEnabled = process.env.VUE_APP_WHITE_LABEL?.toLowerCase() === "true";
  },

  mounted() {
    const gl = document.createElement("canvas").getContext("webgl2");
    if (!gl) {
      if (typeof WebGL2RenderingContext !== "undefined") {
        alert(
          "Pointr needs WebGL2 to work, your browser appears to support WebGL2 but it might be disabled. Try updating your OS and/or video card drivers."
        );
      } else {
        alert("Pointr needs WebGL2 to work, your browser has no WebGL2 support.");
      }
    }
  },

  methods: {
    handleToggleMouseMoveHighlight(value) {
      this.shouldDisableMouseMoveHighlight = value;
    },
    onMapContextMenu({ position, features }) {
      this.contextMenuPosition = position;
      this.contextMenuFeatures = features || [];
    },
    removePopupAndHighlight() {
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
      this.contextMenuSelectedFid = null;
    },
    handleContextMenuFeatureSelected({ feature }) {
      if (!feature) {
        this.contextMenuSelectedFid = null;
        return;
      }
      const fid = feature.properties?.fid;
      this.contextMenuSelectedFid = fid !== undefined ? fid : null;
    },
    handleContextMenuCopySelectedFeatures() {
      const roundTo8 = (n) => parseFloat(Number(n).toFixed(8));
      const normalizeCoords = (coords) => {
        if (!Array.isArray(coords)) return coords;
        // Point [lng, lat]
        if (coords.length === 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
          return [roundTo8(coords[0]), roundTo8(coords[1])];
        }
        // Nested arrays (LineString, Polygon, MultiPolygon)
        return coords.map((entry) => normalizeCoords(entry));
      };
      let features = [];
      this.selectedFeatures.forEach((feature) => {
        const cloned = JSON.parse(JSON.stringify(feature));
        delete cloned.properties.fid;
        if (cloned.geometry && cloned.geometry.coordinates) {
          cloned.geometry.coordinates = normalizeCoords(cloned.geometry.coordinates);
        }
        features.push(cloned);
      });
      this.$store.commit("CONTENT/COPIED_MAP_CONTENT_FEATURES", features);
      const textToCopy = JSON.stringify(features);
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(textToCopy).catch((err) => console.error("Clipboard error", err));
      }
      this.guidanceText = this.$t("multiple-selection.items-copied", { count: features.length });
      // Close popup after copy
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    handleContextMenuCopy({ feature }) {
      if (!feature) return;

      const roundTo8 = (n) => parseFloat(Number(n).toFixed(8));
      const normalizeCoords = (coords) => {
        if (!Array.isArray(coords)) return coords;
        // Point [lng, lat]
        if (coords.length === 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
          return [roundTo8(coords[0]), roundTo8(coords[1])];
        }
        // Nested arrays (LineString, Polygon, MultiPolygon)
        return coords.map((entry) => normalizeCoords(entry));
      };
      const cloned = JSON.parse(JSON.stringify(feature));
      delete cloned.properties.fid;
      if (cloned.geometry && cloned.geometry.coordinates) {
        cloned.geometry.coordinates = normalizeCoords(cloned.geometry.coordinates);
      }
      this.$store.dispatch("CONTENT/CLEAR_SELECTED_FEATURES");
      this.$store.commit("CONTENT/COPIED_MAP_CONTENT_FEATURES", [cloned]);

      const textToCopy = JSON.stringify(cloned);
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(textToCopy).catch((err) => console.error("Clipboard error", err));
      }
      this.guidanceText = this.$t("multiple-selection.items-copied", { count: 1 });
      // Close popup after copy
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    async handleContextMenuPasteAndEdit({ features, position }) {
      if (!features || features.length === 0 || !position) return;
      this.contextMenuSelectedFid = null;
      const to8Decimals = (n) => Number(n).toFixed(8);
      const pasteLng = position.lng;
      const pasteLat = position.lat;
      const currentSite = this.currentSite;
      const currentBuilding = this.currentBuildingObject?.properties?.fid;
      const currentLevel = this.$store.state.MAP.currentLevel;
      const clientId = this.currentClient;

      if (
        pasteLng == null ||
        pasteLat == null ||
        currentSite === undefined ||
        currentBuilding === undefined ||
        currentLevel === undefined ||
        !clientId
      ) {
        return;
      }

      if (features.length === 1) {
        this.$router
          .push({
            name: "MapContentAdd",
            params: {
              siteId: currentSite,
              buildingId: currentBuilding,
              levelId: currentLevel
            },
            query: {
              pasteLng: to8Decimals(pasteLng),
              pasteLat: to8Decimals(pasteLat)
            }
          })
          .catch((e) => console.log(e.message));
      } else {
        // Compute offset from first feature's center to paste position; apply same offset to all so relative positions are preserved
        const firstCenter = MapHelpers.getGeometryCenter(features[0]?.geometry);
        const offset = firstCenter ? [pasteLng - firstCenter[0], pasteLat - firstCenter[1]] : [0, 0];

        const featuresToAdd = features.map((feature, index) => {
          const props = { ...feature.properties };
          delete props.fid;
          props.sid = currentSite;
          props.bid = currentBuilding;
          props.lvl = currentLevel;

          let geometry;
          if (feature.geometry?.type === "Point") {
            const [lng, lat] = feature.geometry.coordinates || [];
            const target = index === 0 ? [pasteLng, pasteLat] : [lng + offset[0], lat + offset[1]];
            geometry = { type: "Point", coordinates: target };
          } else if (feature.geometry?.type === "Polygon" || feature.geometry?.type === "MultiPolygon") {
            const c = MapHelpers.getGeometryCenter(feature.geometry);
            const targetCenter =
              index === 0 ? [pasteLng, pasteLat] : c ? [c[0] + offset[0], c[1] + offset[1]] : [pasteLng, pasteLat];
            geometry = MapHelpers.moveGeometryToCenter(feature.geometry, targetCenter);
          } else {
            return;
          }
          return { type: "Feature", properties: props, geometry };
        });
        this.$store.commit("CONTENT/COPIED_MAP_CONTENT_FEATURES", featuresToAdd);
        this.$router
          .push({
            name: "MapContentMultipleAdd",
            params: {
              siteId: currentSite,
              buildingId: currentBuilding,
              levelId: currentLevel
            },
            query: { isPasted: true }
          })
          .catch((e) => console.log(e.message));
      }
      this.$store.dispatch("CONTENT/CLEAR_SELECTED_FEATURES");
      this.guidanceText = this.$t("multiple-selection.items-on-clipboard", { count: features.length });
      // Close popup after navigating to edit
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    handleContextMenuEdit({ feature }) {
      if (!feature) return;
      const { sid, bid, lvl, fid } = feature.properties || {};
      if (sid === undefined || bid === undefined || lvl === undefined || !fid) {
        return;
      }
      this.$router
        .push({
          name: "MapContentEdit",
          params: {
            siteId: sid,
            buildingId: bid,
            levelId: lvl,
            featureId: fid
          }
        })
        .catch((e) => console.log(e.message));
      // Close popup after navigating to edit
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    handleContextMenuMultipleEdit({ features }) {
      if (!features || features.length === 0) {
        return;
      }
      this.$router
        .push({
          name: "MapContentMultipleEdit",
          params: {
            features: features
          }
        })
        .catch((e) => console.log(e.message));
      // Close popup after navigating to multiple edit
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    handleContextMenuMultipleClone({ features, position }) {
      if (!features || features.length === 0) {
        return;
      }
      this.$router
        .push({
          name: "MapContentMultipleAdd"
        })
        .catch((e) => console.log(e.message));
      // Close popup after navigating to multiple edit
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    handleContextMenuClone({ feature, position }) {
      if (!feature || !position) return;
      const cloned = JSON.parse(JSON.stringify(feature));
      delete cloned.properties.fid;
      this.$store.commit("CONTENT/COPIED_MAP_CONTENT_FEATURES", [cloned]);
      const { sid, bid, lvl } = feature.properties || {};
      const offsetDeg = 0.000005;
      const pasteLng = (position.lng ?? 0) + offsetDeg;
      const pasteLat = (position.lat ?? 0) + offsetDeg;

      if (sid === undefined || bid === undefined || lvl === undefined) {
        return;
      }
      const to8Decimals = (n) => Number(n).toFixed(8);
      this.$router
        .push({
          name: "MapContentAdd",
          params: {
            siteId: sid,
            buildingId: bid,
            levelId: lvl
          },
          query: {
            pasteLng: to8Decimals(pasteLng),
            pasteLat: to8Decimals(pasteLat)
          }
        })
        .catch((e) => console.log(e.message));
      // Close popup after navigating to add
      this.contextMenuPosition = null;
      this.contextMenuFeatures = [];
    },
    setMode(mode) {
      this.$store.commit("MAP/MAP_MODE", this.mapMode === mode ? MapModeConstants.EMPTY_MODE : mode);
    },
    deleteAll() {
      PointMode.clearPoints();
      GraphMode.clear({ shouldFireDeleteEvent: true });
      this.$store.commit("MAP/MAP_MODE", MapModeConstants.EMPTY_MODE);
      this.$store.dispatch("MAP/DRAWN_POLYGON_CHANGED", { feature: undefined, shouldKeepFormClean: true });
      this.$store.commit("MAP/DRAWN_COORDINATES", undefined);
      this.$store.commit("CONTENT/IS_FORM_DIRTY", true);
      // Currently used to reset graph form
      if (this.$refs.contentPage.clearForm) {
        this.$refs.contentPage.clearForm();
        this.$store.commit("CONTENT/SELECTED_CONTENT", undefined);
      }
    },
    zoomIn() {
      this.$store.commit("MAP/ZOOM", this.zoom + 1);
    },
    zoomOut() {
      this.$store.commit("MAP/ZOOM", this.zoom - 1);
    },
    locateMe() {
      const coordinates =
        this.currentBuildingObject?.geometry?.coordinates[0] || this.currentSiteObject?.geometry?.coordinates[0];
      MapHelpers.fitBounds(coordinates);
    },
    toggleMenu() {
      this.isMenuShrunk = !this.isMenuShrunk;
      this.animateMap({
        padding: this.isMenuShrunk ? -320 : 0,
        transitionDuration: 500,
        shouldCheckIfResizeRequired: this.isMenuShrunk
      });
    },
    animateMap(options) {
      const { padding, transitionDuration, shouldCheckIfResizeRequired } = options;
      this.map.easeTo({
        padding: padding,
        duration: transitionDuration // In ms. This matches the CSS transition duration property.
      });
      // Resize map if necessary
      if (shouldCheckIfResizeRequired) {
        const checkInterval = 10;
        let interval = setInterval(() => {
          const canvasContainerWidth = document.getElementsByClassName("maplibregl-canvas-container")[0].offsetWidth;
          const canvasElWidth = document.getElementsByClassName("maplibregl-canvas")[0].offsetWidth;

          if (canvasContainerWidth > canvasElWidth) {
            this.map.resize();
          }
        }, checkInterval);

        setTimeout(() => {
          clearInterval(interval);
        }, transitionDuration + 100);
      }
    },
    onFileDrop(event) {
      event.preventDefault();
      let files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        this.$store.commit("CONTENT/IS_DRAGGING", false);
        return;
      }
      this.$refs.contentAddEditPanel.onDropFile(files[0]);
    },

    onMapSelectionChanged() {
      const selectedCount = this.$store.state.CONTENT.selectedFeatures.length;
      const isShiftActive = this.guidanceMessage === this.$t("multiple-selection.tooltips.shift");
      if (selectedCount === 0) {
        this.guidanceText = undefined;
      } else {
        this.guidanceText = `${this.$t("multiple-selection.items-selected", { count: selectedCount })}${this.$t(
          "multiple-selection.keep-pressing-shift"
        )}`;
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/scss/variables.scss";
// Adds opacity to list elements at clients, sites, buildings, levels pages but not to nested list of mapDesigner page
$inner-left-content-width: $left-content-width - 2 * 16;

.content-list.v-list {
  .v-list-item {
    border: 1px solid transparent !important;
    border-radius: 8px !important;

    &::before {
      border-radius: 8px;
    }

    &__action:last-of-type:not(:only-child) {
      margin-left: auto;
      min-width: 220px !important;
      align-items: center;

      button {
        margin-left: 8px;
      }
    }

    &__title {
      font-size: #{$font-size-3};
      line-height: #{$line-height-15};
      color: var(--v-neutral-darken3);
    }

    &__subtitle {
      font-size: #{$font-size-1} !important;
      line-height: #{$line-height-xs};
      color: var(--v-neutral-base) !important;
    }
  }

  &:not(.nested-groups) {
    .v-list-item {
      &::before {
        opacity: 0 !important;
      }
    }
  }

  &:not(.v-list--flat) {
    .v-list-item {
      &:hover {
        border: 1px solid var(--v-primary-lighten3) !important;
      }

      &:not(.content):active {
        border: 1px solid var(--v-primary-base) !important;

        .v-list-item__title {
          color: var(--v-primary-base) !important;
        }
      }
    }
  }
}

.left-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  box-shadow: 0px 4px 8px rgba(23, 25, 28, 0.16);
  width: #{$left-content-width};
  transition: width 0.5s linear;
  position: relative;
  overflow: hidden;

  .topic {
    font-size: #{$font-size-6};
    font-weight: #{$font-weight-medium};
    line-height: #{$line-height-10};
    color: var(--v-neutral-darken5);

    &.larger-font {
      font-size: #{$font-size-8};
      line-height: 32px;
      font-weight: 400;
    }

    .thinner-font {
      font-weight: 400;
    }
  }

  .sub-topic {
    font-weight: 400;
    color: var(--v-neutral-darken1);
    min-width: $inner-left-content-width;
    flex-shrink: 0;
  }

  .count-info {
    font-size: #{$font-size-2};
    color: var(--v-neutral-base);
    line-height: #{$line-height-15};
    min-width: $inner-left-content-width;
    flex-shrink: 0;
  }

  .header {
    width: $inner-left-content-width;
    flex-shrink: 0;
  }

  > * {
    &:not(.left-menu-expander) {
      opacity: 1;
      transition: opacity 0.5s ease-in-out;
    }
  }

  &.shrunk {
    padding: 0;
    width: #{$menu-expander-width};
    transition: width 0.5s linear;

    > * {
      &:not(.left-menu-expander):not(.form-view) {
        opacity: 0;
        transition: opacity 0.5s ease-in-out;
        pointer-events: none;
      }
    }

    .form-view {
      > * {
        &:not(.left-menu-expander) {
          opacity: 0;
          transition: opacity 0.5s ease-in-out;
          pointer-events: none;
        }
      }
    }
  }

  .left-content__header,
  .content-list,
  .filter,
  .topic {
    width: #{$inner-left-content-width};
  }

  .form-view {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-grow: 1 !important;
  }

  .left-content__header {
    min-height: 46px !important;
    height: 46px;
    display: flex;

    > :nth-child(3) {
      flex-grow: 1;
    }
  }

  > .v-card {
    &.pa-4 {
      &.ms-n4 {
        min-width: calc(100% + 32px);
      }
    }
  }
}

.content-view {
  display: flex;
  height: 100%;
  overflow-x: hidden;

  .v-btn:not(.v-btn--round).v-size--default {
    min-width: 40px;
    min-height: 40px;
    height: auto;
  }

  div.border {
    position: absolute;
    display: none;
    z-index: 2;

    &.top {
      top: 0;
      right: 0;
      width: calc(100% - #{$content-add-edit-panel-width});
      height: 4px;

      &.no-add-edit-panel {
        width: 100%;
      }
    }

    &.bottom {
      bottom: 0;
      right: 0;
      width: calc(100% - #{$content-add-edit-panel-width});
      height: 4px;

      &.no-add-edit-panel {
        width: 100%;
      }
    }

    &.top,
    &.bottom {
      border-top: 2px dashed var(--v-alert-darken1);
      border-bottom: 2px dashed var(--v-alert-darken1);
    }

    &.left {
      top: 0;
      left: #{$content-add-edit-panel-width};
      width: 4px;
      height: 100%;

      &.no-add-edit-panel {
        left: 0;
      }
    }

    &.right {
      bottom: 0;
      right: 0;
      width: 4px;
      height: 100%;
    }

    &.left,
    &.right {
      border-left: 2px dashed var(--v-alert-darken1);
      border-right: 2px dashed var(--v-alert-darken1);
    }
  }

  .map-edit-border {
    div.border {
      display: block;
    }
  }

  .right-content {
    width: 100%;
    position: relative;
    min-width: 270px;
    display: flex;

    .map-wrapper {
      position: relative;
      width: 100%;
      flex-shrink: 0;
      overflow: hidden;

      &.hidden {
        visibility: hidden;
      }
    }
  }
}

#drop-area {
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 9;
}

.ai-in-progress {
  position: absolute !important;
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(52, 109, 241, 0.5);
  z-index: 1 !important;
}

.ai-in-progress::before {
  content: " ";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 10px;
  background: var(--v-primary-lighten5);
  box-shadow: 0px 0px 26px rgba(255, 255, 255, 0.95);
  animation: x 3s linear infinite;
  animation-direction: alternate;
}

.copyright-area {
  display: inline-flex;
  margin-bottom: -8px;
  margin-left: -60px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;

  .copyright-text {
    color: var(--v-neutral-darken1);
    text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;
    font-family: Arial, sans-serif;
    font-size: 10px;
    font-weight: 700;
    line-height: 10px;
    /* 100% */
    letter-spacing: -0.408px;
    text-align: center;
  }
}

@keyframes x {
  to {
    transform: translateX(-100%);
    left: 100%;
  }
}
</style>
